import { useLingui } from '@lingui/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  EMPTY_MATH_LIBRARY,
  groupMathLibrary,
  loadMathLibrary,
  MATH_LIBRARY_HANDLE,
  type MathGroup,
  type MathPracticeSheet,
  mathLibraryTypes,
  openMathGroup,
  pickMathSheet,
} from '../content/math-library';
import { usedLineages } from '../content/sessions';
import { nextUnused } from '../content/workbook';
import {
  lastMathSet,
  MATH_CATEGORIES,
  MATH_CATEGORY_LABEL,
  MATH_LEVEL_LABEL,
  type MathCategory,
  type MathLevel,
} from '../domain/math';
import { queryKeys } from '../query/keys';
import { useConfig, useSession } from './provider';
import { useLocalSessions } from './use-local-sessions';

const DAY = 24 * 60 * 60_000;

/** A row of Library › Math, translated for the screens. */
export interface MathEntry {
  lineageUuid: string;
  /** The version shown: what Saved points at. */
  uuid: string;
  /** Every lineage that opens it, its own first: what Saved or an exercise left off may hold. */
  lineages: string[];
  title: string;
  category: MathCategory;
  /** The category's name: the row's byline. */
  byline: string;
  /** The levels' names: the row's tags. */
  tags: string[];
  /** The illustration's name ("small-numbers", "dice"). */
  icon?: string;
  summary?: string;
}

function useMathLibraryQuery() {
  const session = useSession();
  const { strongerMemoryTypes, strongerMemoryGroupActorUuid } = useConfig();
  const types = mathLibraryTypes(strongerMemoryTypes);
  const query = useQuery({
    queryKey: queryKeys.library(MATH_LIBRARY_HANDLE),
    enabled: session.status === 'authenticated' && types !== undefined,
    staleTime: DAY,
    queryFn: () =>
      loadMathLibrary(types as NonNullable<typeof types>, strongerMemoryGroupActorUuid),
  });
  const groups = useMemo(() => groupMathLibrary(query.data ?? EMPTY_MATH_LIBRARY), [query.data]);
  return { query, groups, available: types !== undefined };
}

/**
 * Library › Math: the Math library's sheets from Core, grouped into the design's rows, with the
 * categories that hold one. `ready` like `useLibrary`: until Core has answered, an empty shelf
 * means "not yet".
 */
export function useMathLibrary() {
  const { i18n } = useLingui();
  const { query, groups, available } = useMathLibraryQuery();
  const entries = useMemo(
    (): MathEntry[] =>
      groups.map((group) => {
        const tags = group.levels.flatMap((level) => {
          const label = MATH_LEVEL_LABEL[level];
          return label ? [i18n._(label)] : [];
        });
        return {
          lineageUuid: group.lineageUuid,
          uuid: group.uuid,
          lineages: [...group.lineages, ...group.savedAs],
          title: i18n._(group.title),
          category: group.category,
          byline: i18n._(MATH_CATEGORY_LABEL[group.category]),
          tags,
          ...(group.icon ? { icon: group.icon } : {}),
          ...(group.summary ? { summary: group.summary } : {}),
        };
      }),
    [groups, i18n],
  );
  const categories = useMemo(() => {
    const used = new Set(entries.map((entry) => entry.category));
    return MATH_CATEGORIES.filter((key) => used.has(key)).map((key) => ({
      key,
      label: i18n._(MATH_CATEGORY_LABEL[key]),
    }));
  }, [entries, i18n]);
  return {
    /** The environment has the Math library: Library shows its Math shelf. */
    available,
    entries,
    /** The row a lineage opens: its own, or a Core row's that starts in it. */
    entryFor: (lineageUuid: string): MathEntry | undefined =>
      entries.find((entry) => entry.lineages.includes(lineageUuid)),
    /** The rows these lineages open, each once, in the order given. */
    entriesFor: (lineages: readonly string[]): MathEntry[] => [
      ...new Set(
        lineages.flatMap(
          (lineage) => entries.find((entry) => entry.lineages.includes(lineage)) ?? [],
        ),
      ),
    ],
    categories,
    ready: !available || query.data !== undefined || query.isError,
    /** Core could not be read, and there's nothing cached to show instead. */
    failed: query.isError && query.data === undefined,
    retry: () => void query.refetch(),
  };
}

/**
 * A row chosen in Library › Math, for the exercise: the row and its first sheet the person
 * hasn't done (past the last, the first again), or the sheet the lineage names. `loading`
 * until Core has answered; `set` undefined when the lineage isn't in the library.
 */
export function useMathSet(lineageUuid: string | undefined): {
  set: (Omit<MathGroup, 'title'> & { title: string }) | undefined;
  sheet: MathPracticeSheet | undefined;
  loading: boolean;
} {
  const { i18n } = useLingui();
  const { query, groups } = useMathLibraryQuery();
  const sessions = useLocalSessions();
  const opened = lineageUuid ? openMathGroup(groups, lineageUuid) : undefined;
  const group = opened?.group;
  const set = useMemo(
    () => (group ? { ...group, title: i18n._(group.title) } : undefined),
    [group, i18n],
  );
  const start = opened?.sheet;
  const sheet = useMemo(
    () =>
      start ??
      (set ? nextUnused(set.sheets, usedLineages(sessions.data ?? [], 'math')) : undefined),
    [start, set, sessions.data],
  );
  const loading =
    lineageUuid !== undefined &&
    ((query.isPending && query.fetchStatus !== 'idle') ||
      (sessions.isPending && sessions.fetchStatus !== 'idle'));
  return { set, sheet, loading };
}

/**
 * Quick math's "What would you like to practice?": the row the person practised last
 * ("Do more of…", by lineage, undefined before their first), and "Pick for me" at a level.
 * `available` is false where the environment has no Math library, or it is empty: the
 * exercise then goes straight on, on the workbook. `ready` once Core has answered.
 */
export function useMathChoice() {
  const library = useMathLibrary();
  const { query, groups: sets } = useMathLibraryQuery();
  const sessions = useLocalSessions();
  const sessionsReady = sessions.data !== undefined || sessions.isError;
  const last = useMemo(() => {
    const attempts = (sessions.data ?? []).flatMap((session) =>
      session.attempts.map((attempt) => ({
        kind: attempt.kind,
        startedAt: attempt.value.startedAt,
        ...(attempt.value.completedAt ? { completedAt: attempt.value.completedAt } : {}),
        includes: attempt.includes,
      })),
    );
    const set = lastMathSet(sets, attempts);
    return set ? library.entries.find((entry) => entry.lineageUuid === set.lineageUuid) : undefined;
  }, [sets, sessions.data, library.entries]);
  return {
    ready: library.ready && sessionsReady,
    available: library.entries.length > 0,
    last,
    /** The sheet "Pick for me" starts on at `level`, by lineage: `useMathSet` opens its row. */
    pick: (level: MathLevel): string | undefined =>
      pickMathSheet(
        query.data ?? EMPTY_MATH_LIBRARY,
        level,
        usedLineages(sessions.data ?? [], 'math'),
      )?.lineageUuid,
  };
}
